import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
} from '@mui/material';
import {
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Api as ApiIcon,
  SmartToy as AiIcon,
  Loop as LoopIcon,
  AccountTree as FlowIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Storage as StorageIcon,
  Psychology as PsychologyIcon,
  Hub as HubIcon,
  IntegrationInstructions as IntegrationIcon,
  Chat as ChatIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  DataObject as DataIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Cloud as CloudIcon,
  LocalOffer as TagIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ArrowForward as ArrowIcon,
  CallSplit as SplitIcon,
  CallMerge as MergeIcon,
  Timer as TimerIcon,
  Refresh as RetryIcon,
  Input as FormIcon,
  Person as PersonIcon,
  AutoAwesome as AutoIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Group as GroupIcon,
  Verified as VerifiedIcon,
  Folder as FolderIcon,
  ToggleOn as SwitchIcon,
  BugReport as DebugIcon,
  Sms as SmsIcon,
  Notifications as NotificationIcon,
  Message as MessageIcon,
  Call as CallIcon,
  Publish as PublishIcon,
  TouchApp as TriggerIcon,
  Schedule as ScheduleIcon,
  Pause as PauseIcon,
  Router as RouterIcon,
  Stop as StopIcon,
  Notes as LoggerIcon,
  BugReport as DebuggerIcon,
  Assessment as AssessmentIcon,
  CleaningServices as CleanupIcon,
  Download as DownloadIcon,
  SkipNext as SkipNextIcon,
  Timeline as TimelineIcon,
  Monitor as MonitorIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import ErrorBoundary from '../components/ErrorBoundary';
import NodeDocumentationHub from './NodeDocumentationHub';

function AgenticWorkflowHelp() {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabLabels = [
    'Getting Started',
    'Node Types & Features',
    'Node Documentation Hub',
    'Node Support Status',
    'Edge Types & Control Flow',
    'Real-Time Streaming',
    'Error Recovery & Monitoring',
    'Performance & Caching',
    'State Management',
    'Form Integration',
    'Performance & Optimization',
    'Best Practices',
    'Troubleshooting',
  ];

  return (
    <ErrorBoundary>
      <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
            }}
          >
            <FlowIcon sx={{ fontSize: '2.5rem' }} />
            Workflow Help
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Complete guide to building and debugging agentic workflows
          </Typography>
        </Box>

        {/* Tab Navigation */}
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
              },
            }}
          >
            {tabLabels.map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box sx={{ minHeight: '600px' }}>

          {/* Tab 0: Getting Started */}
          {selectedTab === 0 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <InfoIcon />
              {' '}
              Getting Started
            </Typography>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>🚀 Latest Enhancements:</strong>
                {' '}
                Now featuring real-time streaming, advanced error recovery, comprehensive monitoring, and intelligent performance optimization!
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>What is Agentic Flow Builder?</Typography>
                    <Typography variant="body1" paragraph>
                      Agentic Flow Builder is a powerful visual workflow designer that enables you to create,
                      execute, and debug complex agentic workflows using LangGraph's native features.
                      It provides a drag-and-drop interface for building workflows with AI agents, code execution,
                      API calls, and advanced control flows.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Key Features:</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><AiIcon color="primary" /></ListItemIcon>
                        <ListItemText primary="AI Agent Integration" secondary="Seamless integration with multiple LLM providers" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><DebugIcon color="secondary" /></ListItemIcon>
                        <ListItemText primary="Advanced Debugging" secondary="Built-in Logger and Debugger nodes with real-time inspection" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TimelineIcon color="success" /></ListItemIcon>
                        <ListItemText primary="Visual Execution Tracking" secondary="Real-time execution monitoring and flow visualization" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><FormIcon color="warning" /></ListItemIcon>
                        <ListItemText primary="Form Integration" secondary="Consume and render forms created with FormBuilder" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Quick Start Guide</Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2">1. Create a New Flow</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Click "New Flow" and select a template or start from scratch
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">2. Add Nodes</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Drag and drop nodes from the palette onto the canvas at your desired location
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">3. Connect Nodes</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Click on nodes to create connections between them
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2">4. Configure & Execute</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Configure node settings and run your workflow
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="subtitle2">💡 Pro Tip</Typography>
              <Typography variant="body2">
                Start with simple flows and gradually add complexity. Use the debugging features
                to understand execution flow and troubleshoot issues.
              </Typography>
            </Alert>
          </Box>
          )}

          {/* Tab 1: Node Types & Features */}
          {selectedTab === 1 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HubIcon />
              {' '}
              Node Types & Features
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AiIcon color="primary" />
                      {' '}
                      AI Agent Node
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Process data using Large Language Models with intelligent provider routing
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip label="Smart Routing" size="small" color="primary" sx={{ mr: 1 }} />
                      <Chip label="Fallback Support" size="small" color="secondary" />
                    </Box>
                    <Typography variant="subtitle2" gutterBottom>Configuration Options:</Typography>
                    <List dense>
                      <ListItem><ListItemText primary="Model Selection (Llama, GPT, Claude)" /></ListItem>
                      <ListItem><ListItemText primary="System Prompts" /></ListItem>
                      <ListItem><ListItemText primary="Temperature Control" /></ListItem>
                      <ListItem><ListItemText primary="Max Tokens" /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LoggerIcon color="success" />
                      {' '}
                      Logger Node
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Comprehensive logging for execution monitoring and debugging
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip label="Execution Tracking" size="small" color="success" sx={{ mr: 1 }} />
                      <Chip label="Performance Metrics" size="small" color="info" />
                    </Box>
                    <Typography variant="subtitle2" gutterBottom>Log Types:</Typography>
                    <List dense>
                      <ListItem><ListItemText primary="Execution Logs - Node execution events" /></ListItem>
                      <ListItem><ListItemText primary="Data Logs - State variable changes" /></ListItem>
                      <ListItem><ListItemText primary="Performance Logs - Timing metrics" /></ListItem>
                      <ListItem><ListItemText primary="Error Logs - Exception tracking" /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DebuggerIcon color="warning" />
                      {' '}
                      Debugger Node
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Advanced debugging with breakpoints and state inspection
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip label="Breakpoints" size="small" color="warning" sx={{ mr: 1 }} />
                      <Chip label="State Inspection" size="small" color="secondary" />
                    </Box>
                    <Typography variant="subtitle2" gutterBottom>Debug Features:</Typography>
                    <List dense>
                      <ListItem><ListItemText primary="Conditional Breakpoints" /></ListItem>
                      <ListItem><ListItemText primary="State Variable Inspection" /></ListItem>
                      <ListItem><ListItemText primary="Execution Path Tracing" /></ListItem>
                      <ListItem><ListItemText primary="Auto-continue Options" /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormIcon color="info" />
                      {' '}
                      Form Node
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Human interaction through dynamic forms created with FormBuilder
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip label="FormBuilder Integration" size="small" color="info" sx={{ mr: 1 }} />
                      <Chip label="Dynamic Rendering" size="small" color="primary" />
                    </Box>
                    <Typography variant="subtitle2" gutterBottom>Capabilities:</Typography>
                    <List dense>
                      <ListItem><ListItemText primary="Consume FormBuilder forms" /></ListItem>
                      <ListItem><ListItemText primary="Dynamic field mapping" /></ListItem>
                      <ListItem><ListItemText primary="Validation support" /></ListItem>
                      <ListItem><ListItemText primary="State variable integration" /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 2: Node Documentation Hub */}
          {selectedTab === 2 && (
            <Box sx={{ mt: -3, mx: -3 }}>
              <NodeDocumentationHub />
            </Box>
          )}

          {/* Tab 3: Node Support Status */}
          {selectedTab === 3 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CheckIcon />
              {' '}
              Node Support Status
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">📋 Support Status Legend</Typography>
              <Typography variant="body2">
                <strong>✅ Fully Supported:</strong>
                {' '}
                Complete implementation with dedicated functionality
                <br />
                <strong>⚠️ Stub Implementation:</strong>
                {' '}
                Basic placeholder - uses generic node functionality
                <br />
                <strong>🚧 In Development:</strong>
                {' '}
                Currently being implemented
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {/* AI & Logic Nodes */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AiIcon color="primary" />
                      {' '}
                      AI & Logic Nodes
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="AI Agent"
                          secondary="✅ Fully supported - LLM integration with multiple providers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Code Executor"
                          secondary="✅ Fully supported - Execute custom Python/JavaScript code"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Decision Tree"
                          secondary="⚠️ Stub implementation - Uses generic node"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Conditional"
                          secondary="✅ Fully supported - Conditional logic and branching"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Loop"
                          secondary="✅ Fully supported - Iterative execution with conditions"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Switch"
                          secondary="⚠️ Stub implementation - Uses generic node"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Try-Catch"
                          secondary="⚠️ Stub implementation - Uses generic node"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Data & API Nodes */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ApiIcon color="secondary" />
                      {' '}
                      Data & API Nodes
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="RAG Retriever"
                          secondary="✅ Fully supported - Vector search and retrieval"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="API Call"
                          secondary="✅ Fully supported - HTTP requests with authentication"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Webhook"
                          secondary="✅ Fully supported - Incoming webhook handling"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Database"
                          secondary="✅ Fully supported - Database operations"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Form"
                          secondary="✅ Fully supported - FormBuilder integration"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Communication Nodes */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChatIcon color="info" />
                      {' '}
                      Communication Nodes
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Email"
                          secondary="✅ Fully supported - Send emails with templates"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="SMS"
                          secondary="✅ Fully supported - Send SMS messages"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Chat"
                          secondary="✅ Fully supported - Chat interface integration"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Human Input"
                          secondary="✅ Fully supported - Human-in-the-loop interactions"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Calendar"
                          secondary="✅ Fully supported - Calendar operations"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Notification"
                          secondary="✅ Fully supported - Push notifications"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Message Queue"
                          secondary="✅ Fully supported - Message queuing"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Voice Call"
                          secondary="✅ Fully supported - Voice call integration"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Social Post"
                          secondary="✅ Fully supported - Social media posting"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* AI & Memory Nodes */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MemoryIcon color="warning" />
                      {' '}
                      AI & Memory Nodes
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Memory"
                          secondary="✅ Fully supported - Persistent memory storage"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Tool"
                          secondary="✅ Fully supported - Custom tool integration"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Control Flow Nodes */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlowIcon color="primary" />
                      {' '}
                      Control Flow Nodes
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Trigger"
                          secondary="✅ Fully supported - Event-based triggers"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Scheduler"
                          secondary="✅ Fully supported - Time-based scheduling"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Delay"
                          secondary="✅ Fully supported - Execution delays"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Parallel"
                          secondary="✅ Fully supported - Concurrent execution"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Join"
                          secondary="✅ Fully supported - Synchronization point"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Router"
                          secondary="⚠️ Stub implementation - Uses generic node"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Development Tools */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DebugIcon color="error" />
                      {' '}
                      Development Tools
                    </Typography>

                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Logger"
                          secondary="✅ Fully supported - Comprehensive logging"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Debugger"
                          secondary="✅ Fully supported - Breakpoints and state inspection"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="subtitle2">⚠️ Stub Implementation Notice</Typography>
              <Typography variant="body2">
                Nodes marked as "Stub Implementation" are currently using generic placeholder functionality.
                They will execute but won't provide their specific features (e.g., Decision Tree won't perform
                actual decision logic, Switch won't route based on conditions). These are planned for future
                implementation.
              </Typography>
            </Alert>
          </Box>
          )}

          {/* Tab 4: Edge Types & Control Flow */}
          {selectedTab === 4 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <RouterIcon />
              {' '}
              Edge Types & Control Flow
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Edge Types Overview</Typography>
                    <Typography variant="body1" paragraph>
                      Edges define the flow of execution between nodes. Different edge types provide
                      different control flow behaviors and visual representations.
                    </Typography>

                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Edge Type</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Visual Style</TableCell>
                            <TableCell>Use Case</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ArrowIcon color="primary" />
                                <strong>Basic Edge</strong>
                              </Box>
                            </TableCell>
                            <TableCell>Simple sequential execution</TableCell>
                            <TableCell>Solid line</TableCell>
                            <TableCell>Linear workflows</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckIcon sx={{ color: 'success.main' }} />
                                <strong>On Success</strong>
                              </Box>
                            </TableCell>
                            <TableCell>Execute only on successful node completion</TableCell>
                            <TableCell>Green dashed line</TableCell>
                            <TableCell>Error handling paths</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorIcon color="error" />
                                <strong>On Error</strong>
                              </Box>
                            </TableCell>
                            <TableCell>Execute only when node fails</TableCell>
                            <TableCell>Red dashed line</TableCell>
                            <TableCell>Exception handling</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LoopIcon color="warning" />
                                <strong>Loop</strong>
                              </Box>
                            </TableCell>
                            <TableCell>Iterative execution with conditions</TableCell>
                            <TableCell>Orange curved line</TableCell>
                            <TableCell>Repetitive tasks</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SplitIcon color="secondary" />
                                <strong>Parallel</strong>
                              </Box>
                            </TableCell>
                            <TableCell>Concurrent execution paths</TableCell>
                            <TableCell>Blue dotted line</TableCell>
                            <TableCell>Independent tasks</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 5: Real-Time Streaming */}
          {selectedTab === 5 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TimelineIcon />
              {' '}
              Real-Time Streaming
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Phase 1 Enhancement:</strong>
                {' '}
                Real-time streaming provides live updates during flow execution with Server-Sent Events (SSE) and WebSocket integration.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SpeedIcon color="primary" />
                      {' '}
                      Streaming Features
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Real-Time Updates"
                          secondary="Live flow execution progress with sub-second latency"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Server-Sent Events"
                          secondary="Efficient one-way streaming from server to client"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="WebSocket Fallback"
                          secondary="Automatic fallback for maximum compatibility"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Connection Management"
                          secondary="Automatic reconnection and error handling"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonitorIcon color="primary" />
                      {' '}
                      Streaming Events
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><PlayIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="node_start"
                          secondary="When a node begins execution"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="node_complete"
                          secondary="When a node finishes successfully"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><PauseIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="form_pause"
                          secondary="When flow pauses for user input"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                        <ListItemText
                          primary="flow_error"
                          secondary="When an error occurs during execution"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Streaming Implementation Example
                    </Typography>
                    <Box sx={{
                      bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem',
                    }}
                    >
                      {`// Frontend streaming integration
const streamFlowExecution = async (flowData) => {
  const response = await fetch('/api/v1/flows/execute/stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(flowData)
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value);
    const lines = chunk.split('\\n');
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = JSON.parse(line.slice(6));
        handleStreamingUpdate(data);
      }
    }
  }
};`}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 6: Error Recovery & Monitoring */}
          {selectedTab === 6 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <RetryIcon />
              {' '}
              Error Recovery & Monitoring
            </Typography>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Phase 2 Enhancement:</strong>
                {' '}
                Advanced error recovery system with intelligent retry mechanisms and comprehensive monitoring dashboard.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RetryIcon color="primary" />
                      {' '}
                      Error Recovery
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><AutoIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Automatic Retry"
                          secondary="Smart retry with exponential backoff"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Checkpoint Recovery"
                          secondary="Resume from last successful state"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><PersonIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Manual Recovery"
                          secondary="User-initiated recovery options"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonitorIcon color="primary" />
                      {' '}
                      Monitoring Features
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><TimelineIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Real-Time Tracking"
                          secondary="Live execution monitoring"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AssessmentIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Performance Metrics"
                          secondary="Detailed execution analytics"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><NotificationIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Alert System"
                          secondary="Proactive error notifications"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SettingsIcon color="primary" />
                      {' '}
                      Recovery Options
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><PlayIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Resume Execution"
                          secondary="Continue from interruption point"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><RefreshIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Restart Flow"
                          secondary="Begin execution from start"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SkipNextIcon color="info" /></ListItemIcon>
                        <ListItemText
                          primary="Skip Failed Node"
                          secondary="Continue with next node"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 7: Performance & Caching */}
          {selectedTab === 7 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SpeedIcon />
              {' '}
              Performance & Caching
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>Phase 4 Enhancement:</strong>
                {' '}
                Comprehensive performance optimization with intelligent caching, database optimization, and resource management.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MemoryIcon color="primary" />
                      {' '}
                      Intelligent Caching
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CloudIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Multi-Layer Cache"
                          secondary="Redis + in-memory LRU caching"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AutoIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Smart Invalidation"
                          secondary="Intelligent cache expiration strategies"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AssessmentIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Performance Analytics"
                          secondary="Cache hit/miss tracking and optimization"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StorageIcon color="primary" />
                      {' '}
                      Database Optimization
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><HubIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Connection Pooling"
                          secondary="Efficient MongoDB connection management"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SpeedIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Query Optimization"
                          secondary="Real-time query performance analysis"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AutoIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Auto-Indexing"
                          secondary="AI-driven index recommendations"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Improvements
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Box sx={{
                          textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1,
                        }}
                        >
                          <Typography variant="h4" color="success.dark">60-80%</Typography>
                          <Typography variant="body2">Faster Response Times</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{
                          textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1,
                        }}
                        >
                          <Typography variant="h4" color="info.dark">50%</Typography>
                          <Typography variant="body2">Resource Reduction</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{
                          textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1,
                        }}
                        >
                          <Typography variant="h4" color="warning.dark">10x</Typography>
                          <Typography variant="body2">Concurrent Users</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{
                          textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1,
                        }}
                        >
                          <Typography variant="h4" color="secondary.dark">95%</Typography>
                          <Typography variant="body2">Cache Hit Rate</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 8: State Management */}
          {selectedTab === 8 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <StorageIcon />
              {' '}
              State Management
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Debug Interface Features</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><PauseIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Execution Pause"
                          secondary="Automatic flow interruption at debugger nodes"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><MonitorIcon color="info" /></ListItemIcon>
                        <ListItemText
                          primary="State Inspection"
                          secondary="Real-time variable and state examination"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><EditIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                          primary="State Modification"
                          secondary="Runtime variable editing and updates"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SkipNextIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Step-through Execution"
                          secondary="Manual execution control node by node"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Debug Controls</Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" color="primary">Resume Execution</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Continue execution from paused state
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="primary">Step to Next Node</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Execute one node at a time for detailed inspection
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="primary">Run to End</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Complete execution without further pauses
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="error.main">Stop Execution</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Forcefully terminate the running workflow
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Breakpoint Management</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Setting Breakpoints</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Click Add Breakpoint in debug interface" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Enter node ID or select from list" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Set conditional expressions (optional)" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Breakpoints persist across executions" />
                          </ListItem>
                        </List>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Breakpoint Types</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Node-specific: Pause at specific nodes" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Conditional: Pause based on state values" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Error breakpoints: Pause on exceptions" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Step breakpoints: Manual stepping" />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 8: Form Integration */}
          {selectedTab === 8 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormIcon />
              {' '}
              Form Integration
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>FormBuilder Integration</Typography>
                    <Typography variant="body1" paragraph>
                      The Agentic Flow Builder seamlessly integrates with FormBuilder, allowing you to
                      create dynamic forms and consume them within your workflows for human interaction.
                    </Typography>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2">📖 Complete Documentation</Typography>
                      <Typography variant="body2">
                        For comprehensive details, see the
                        {' '}
                        <Link href="/RICH_FORMS_SUMMARY.md" target="_blank" color="primary">Rich Forms Summary</Link>
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              {/* API Call State Management */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ApiIcon color="primary" />
                      {' '}
                      API Call State Management
                    </Typography>
                    <Typography variant="body1" paragraph>
                      API calls store response data using multiple storage mechanisms to handle both
                      single calls and complex parallel execution scenarios.
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="h6" gutterBottom color="primary">Node-Specific Storage</Typography>
                        <Box sx={{
                          p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2,
                        }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            api_response_full_&#123;node_id&#125;
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Full response data stored with unique node ID suffix. Contains complete
                          API response including status, headers, and data.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography variant="h6" gutterBottom color="secondary">Response Summary</Typography>
                        <Box sx={{
                          p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2,
                        }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            apiResponse
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Lightweight summary of the most recent API call. Gets overwritten by
                          subsequent API calls. Used in status responses.
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography variant="h6" gutterBottom color="success">Node Outputs</Typography>
                        <Box sx={{
                          p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2,
                        }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            node_outputs[node_id]
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          Dictionary mapping each node ID to its output summary. Preserves
                          responses from all executed nodes.
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Multiple API Calls Example */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SplitIcon color="info" />
                      {' '}
                      Multiple API Calls & Parallel Execution
                    </Typography>

                    <Typography variant="body1" paragraph>
                      When multiple API calls run in parallel, each stores its response with a unique identifier.
                      Here's how to distinguish between different API responses:
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Example Parallel Flow</Typography>
                        <Box sx={{
                          p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2,
                        }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            Start → Parallel → getUserAPI
                            <br />
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;→ getProductsAPI
                            <br />
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;→ getPaymentAPI
                            <br />
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;→ Join → Process
                          </Typography>
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>Access Methods:</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText
                              primary="Custom State Variables"
                              secondary="userData, productList, paymentStatus"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
                            <ListItemText
                              primary="Node-Specific Full Response"
                              secondary="api_response_full_getUserAPI"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><CheckIcon fontSize="small" color="secondary" /></ListItemIcon>
                            <ListItemText
                              primary="Node Outputs Dictionary"
                              secondary="node_outputs['getUserAPI']"
                            />
                          </ListItem>
                        </List>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>State Variable Configuration</Typography>
                        <Box sx={{
                          p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2,
                        }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            &#123;
                            <br />
                            &nbsp;&nbsp;"stateVariables": &#123;
                            <br />
                            &nbsp;&nbsp;&nbsp;&nbsp;"userData": "data.user",
                            <br />
                            &nbsp;&nbsp;&nbsp;&nbsp;"userStatus": "status",
                            <br />
                            &nbsp;&nbsp;&nbsp;&nbsp;"userPreferences": "data.preferences"
                            <br />
                            &nbsp;&nbsp;&#125;
                            <br />
                            &#125;
                          </Typography>
                        </Box>

                        <Typography variant="subtitle2" gutterBottom>Best Practices:</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemIcon><VerifiedIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText primary="Use descriptive node IDs" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><VerifiedIcon fontSize="small" color="warning" /></ListItemIcon>
                            <ListItemText primary="Configure unique state variables" />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><VerifiedIcon fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText primary="Avoid generic variable names" />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* State Structure Example */}
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Resulting State Structure</Typography>
                    <Typography variant="body1" paragraph>
                      After parallel API calls complete, the state contains organized data from all responses:
                    </Typography>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">View Complete State Example</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{
                          p: 2, bgcolor: 'grey.50', borderRadius: 1, overflow: 'auto',
                        }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'pre' }}>
                            {`{
  "variables": {
    // Custom configured variables (recommended access)
    "userData": {"id": 123, "name": "John Doe"},
    "productList": [{"id": 1, "name": "Product A"}],
    "paymentStatus": "active",
    
    // Node-specific full responses (detailed access)
    "api_response_full_getUserAPI": {
      "status": 200,
      "data": {"user": {...}, "preferences": {...}},
      "headers": {...}
    },
    "api_response_full_getProductsAPI": {
      "status": 200,
      "data": {"products": [...], "total": 50},
      "headers": {...}
    },
    
    // Most recent API response summary
    "apiResponse": {...}  // Gets overwritten
  },
  
  "node_outputs": {
    "getUserAPI": {"response": {...}},
    "getProductsAPI": {"response": {...}},
    "getPaymentAPI": {"response": {...}}
  }
}`}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Considerations */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SpeedIcon color="warning" />
                      {' '}
                      Performance Considerations
                    </Typography>

                    <List>
                      <ListItem>
                        <ListItemIcon><MemoryIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Response Size Management"
                          secondary="Summary data in status responses, full data excluded for performance"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><FilterIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                          primary="Automatic Data Filtering"
                          secondary="Large API responses filtered from UI updates automatically"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TimerIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Timeout Configuration"
                          secondary="Individual and parallel timeout settings for optimal performance"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Debugging Multiple API Calls */}
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DebugIcon color="error" />
                      {' '}
                      Debugging Multiple API Calls
                    </Typography>

                    <List>
                      <ListItem>
                        <ListItemIcon><ViewIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Variable Inspection"
                          secondary="Check available state variables and their values"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AssessmentIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                          primary="Node Output Tracking"
                          secondary="Monitor which nodes have executed and their outputs"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Response Availability Check"
                          secondary="Verify specific API responses are available before processing"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Variable Types */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Variable Types & Operations</Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Variable Types</Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon><DataIcon color="primary" /></ListItemIcon>
                            <ListItemText
                              primary="Primitive Types"
                              secondary="Strings, numbers, booleans, null"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><DataIcon color="secondary" /></ListItemIcon>
                            <ListItemText
                              primary="Complex Objects"
                              secondary="JSON objects and arrays from API responses"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><FormIcon color="success" /></ListItemIcon>
                            <ListItemText
                              primary="Form Data"
                              secondary="User input from form nodes"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><AiIcon color="warning" /></ListItemIcon>
                            <ListItemText
                              primary="AI Responses"
                              secondary="Generated content from AI agents"
                            />
                          </ListItem>
                        </List>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>State Operations</Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon><EditIcon color="primary" /></ListItemIcon>
                            <ListItemText
                              primary="Runtime Editing"
                              secondary="Modify variables during debugging sessions"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><ViewIcon color="info" /></ListItemIcon>
                            <ListItemText
                              primary="Variable Inspection"
                              secondary="View current state values and their types"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><TimelineIcon color="success" /></ListItemIcon>
                            <ListItemText
                              primary="Change Tracking"
                              secondary="Monitor variable modifications across nodes"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><DownloadIcon color="secondary" /></ListItemIcon>
                            <ListItemText
                              primary="State Export"
                              secondary="Save state snapshots for analysis"
                            />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 8: Form Integration */}
          {selectedTab === 8 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormIcon />
              {' '}
              Form Integration
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>FormBuilder Integration</Typography>
                    <Typography variant="body1" paragraph>
                      The Agentic Flow Builder seamlessly integrates with FormBuilder, allowing you to
                      create dynamic forms and consume them within your workflows for human interaction.
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Integration Features</Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon><DescriptionIcon color="primary" /></ListItemIcon>
                            <ListItemText
                              primary="Dynamic Form Loading"
                              secondary="Load forms created with FormBuilder"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><AutoIcon color="secondary" /></ListItemIcon>
                            <ListItemText
                              primary="Field Mapping"
                              secondary="Automatic mapping to state variables"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><VerifiedIcon color="success" /></ListItemIcon>
                            <ListItemText
                              primary="Validation Support"
                              secondary="Built-in form validation rules"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon><TimelineIcon color="info" /></ListItemIcon>
                            <ListItemText
                              primary="State Synchronization"
                              secondary="Real-time state variable updates"
                            />
                          </ListItem>
                        </List>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Supported Field Types</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Text Input - Single line text" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Text Area - Multi-line text" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Number Input - Numeric values" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Select Dropdown - Single choice" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Radio Buttons - Single choice" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Checkboxes - Multiple choices" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Date Picker - Date selection" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• File Upload - File attachments" />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 9: Performance & Optimization */}
          {selectedTab === 10 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SpeedIcon />
              {' '}
              Performance & Optimization
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Optimization Tips</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><MemoryIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Memory Management"
                          secondary="Monitor state variable sizes and clean up unused data"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TimelineIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                          primary="Execution Monitoring"
                          secondary="Use Logger nodes to track performance bottlenecks"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><LoopIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Loop Optimization"
                          secondary="Avoid infinite loops with proper termination conditions"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ApiIcon color="info" /></ListItemIcon>
                        <ListItemText
                          primary="API Call Optimization"
                          secondary="Use parallel execution for independent API calls"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Performance Metrics</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><TimerIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Execution Time"
                          secondary="Track total and per-node execution times"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AssessmentIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                          primary="Memory Usage"
                          secondary="Monitor state variable memory consumption"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><RetryIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Retry Counts"
                          secondary="Track API call retries and failures"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Success Rate"
                          secondary="Monitor overall workflow reliability"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 11: Best Practices */}
          {selectedTab === 11 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <VerifiedIcon />
              {' '}
              Best Practices
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Workflow Design Principles</Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>✅ Do's</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Start with simple linear flows and gradually add complexity" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Use descriptive names for nodes and variables" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Add Logger nodes for debugging and monitoring" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Use appropriate edge types for different scenarios" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Test workflows with Debugger nodes before production" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Document complex logic with comments" />
                          </ListItem>
                        </List>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>❌ Don'ts</Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Don't create workflows without error handling" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Don't use generic variable names like 'data' or 'result'" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Don't forget to set up breakpoints for complex flows" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Don't ignore state variable size limits" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Don't deploy without testing all execution paths" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Don't use basic edges for conditional logic" />
                          </ListItem>
                        </List>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Security Considerations</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><SecurityIcon color="error" /></ListItemIcon>
                        <ListItemText
                          primary="Input Validation"
                          secondary="Always validate user inputs and API responses"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SecurityIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Code Execution Safety"
                          secondary="Be cautious with Code Executor nodes and user-provided code"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SecurityIcon color="info" /></ListItemIcon>
                        <ListItemText
                          primary="API Key Management"
                          secondary="Store API keys securely and rotate regularly"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SecurityIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Access Control"
                          secondary="Implement proper authentication and authorization"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

          {/* Tab 11: Troubleshooting */}
          {selectedTab === 12 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon />
              {' '}
              Troubleshooting
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>Common Issues & Solutions</Typography>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Debug Interface Won't Load</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography paragraph>
                          <strong>Problem:</strong>
                          {' '}
                          Clicking the debug button shows "Loading debug data..." indefinitely.
                        </Typography>
                        <Typography paragraph>
                          <strong>Solutions:</strong>
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Check browser console for JavaScript errors" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Verify the execution is actually paused" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Check network tab for failed API requests" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Ensure backend is running on port 8001" />
                          </ListItem>
                        </List>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Nodes Not Connecting Properly</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography paragraph>
                          <strong>Problem:</strong>
                          {' '}
                          Unable to draw connections between nodes.
                        </Typography>
                        <Typography paragraph>
                          <strong>Solutions:</strong>
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Ensure you're dragging from output to input handles" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Check node compatibility for connections" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Try refreshing the page if connections are stuck" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Verify edge types are appropriate for the connection" />
                          </ListItem>
                        </List>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">State Variables Not Updating</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography paragraph>
                          <strong>Problem:</strong>
                          {' '}
                          Changes to state variables aren't reflected in subsequent nodes.
                        </Typography>
                        <Typography paragraph>
                          <strong>Solutions:</strong>
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Check variable naming consistency across nodes" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Use Logger nodes to verify state changes" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Ensure nodes are properly connected in sequence" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Check for JavaScript syntax errors in expressions" />
                          </ListItem>
                        </List>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Performance Issues</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography paragraph>
                          <strong>Problem:</strong>
                          {' '}
                          Workflows running slowly or consuming excessive resources.
                        </Typography>
                        <Typography paragraph>
                          <strong>Solutions:</strong>
                        </Typography>
                        <List dense>
                          <ListItem>
                            <ListItemText primary="• Use parallel edges for independent operations" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Reduce state variable sizes by cleaning up unused data" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Implement proper error handling to avoid retries" />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary="• Monitor execution with Logger nodes" />
                          </ListItem>
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
          )}

        </Box>

        {/* Footer */}
        <Box sx={{
          mt: 6, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center',
        }}
        >
          <Typography variant="body2" color="text.secondary">
            Need help? Check the
            {' '}
            <Link href="#documentation" color="primary">documentation</Link>
            {' '}
            or
            contact
            {' '}
            <Link href="#support" color="primary">support</Link>
            .
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Agentic Flow Builder v2.0 - Built with LangGraph and React
          </Typography>
        </Box>
      </Box>
    </ErrorBoundary>
  );
}

export default AgenticWorkflowHelp;
